<template>
  <transition name="slide-x-transition" mode="out-in">
    <section>
      <header-logged-out />
      <v-container class="header" fill-height>
        <v-row class="mb-2">
          <v-col cols="12">
            <h1 class="header-title optus-highlight optus-highlight--yellow">
              Privacy Policy&nbsp;
            </h1>
            <v-card flat class="mt-4 legal">
              <v-card-text>
                The primary purpose of Kimberlin Education Pty Limited ABN 86
                154 894 470 (we, us, our) is to create and deliver engaging
                education programs in the classroom and digital environments. In
                the process of creating and delivering these programs, we
                collect and manage personal information on a regular basis.<br /><br />
                At all times, we respect your privacy and are committed to
                protecting it.<br /><br />
                This privacy policy outlines how we manage personal information
                including how we collect, use, disclose, and store personal
                information. Because we deal with a range of stakeholders, we
                have created specific privacy collection notices for
                stakeholders we regularly collect personal information from –
                namely, teachers, job applicants, contractors, and students.
                These notices can be found at the end of this privacy policy and
                form part of this privacy policy. These notices provide more
                details on our privacy practices relevant to these stakeholders.
                Therefore, please ensure you read the relevant notice carefully.
                We will also provide you with a copy of the relevant notice (in
                hard copy version or by way of an electronic link) at the time
                we collect your personal information or as soon as practicable
                thereafter. If you do not fall into one of these categories, we
                will still provide a privacy collection notice relevant to you
                at the time we collect your personal information or as soon as
                practicable thereafter.<br /><br />
                By providing your personal information to us, you are consenting
                to our management of your personal information in accordance
                with this privacy policy. There may be times we need to change
                this privacy policy, for instance, in recognition of the dynamic
                environment of the internet or a change in law. Any updated
                versions will be published on our website and we encourage you
                to check our website regularly to ensure you are kept abreast of
                this privacy policy.<br /><br />
                In this privacy policy,
                <strong>personal information</strong> means information or an
                opinion about an individual that is reasonably identifiable.
                This may include, for instance, your name, age, gender, contact
                details as well as financial and health information.<br /><br />
                This policy does not apply to our management of employee records
                which are exempt from the Privacy Act 1988 (Cth). However, we
                treat information collected about our employees with the same
                standard of respect and commitment as we treat confidential
                information.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                What personal information do we collect?
              </h2>
              <v-card-text>
                Generally, we collect the following kinds of personal
                information:
                <ul>
                  <li>name;</li>
                  <li>mailing or street address;</li>
                  <li>email address;</li>
                  <li>telephone and other contact details;</li>
                  <li>age or date of birth; and</li>
                  <li>profession, occupation, or job title.</li>
                </ul>
                <br />
                We may also collect further personal information depending on
                your relationship with us. For instance, some of the kinds of
                personal information we may collect is outlined below.<br /><br />
                <table class="policy-table">
                  <tr>
                    <td><p>For teachers</p></td>
                    <td>
                      <ul style="list-style-type:none;">
                        <li>Current school you teach at.</li>
                        <li>Subjects and grade you teach.</li>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <td><p>Job applicants</p></td>
                    <td>
                      <ul style="list-style-type:none;">
                        <li>Information on job application.</li>
                        <li>Professional development history.</li>
                        <li>
                          Salary and payment information, including
                          superannuation details.
                        </li>
                        <li>
                          Medical and health details (e.g. details of allergies
                          and/or disabilities, medical certificates,
                          pre-employment medical examination aptitude or
                          psychological testing).
                        </li>
                        <li>
                          Work emails and private emails (when using work email
                          address).
                        </li>
                        <li>Internet browsing history</li>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <td><p>Contractors</p></td>
                    <td>
                      <ul style="list-style-type:none;">
                        <li>Information on service delivery.</li>
                        <li>
                          Fee information, including, where relevant
                          superannuation details.
                        </li>
                        <li>Medical and health details.</li>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <td><p>Other People who come into contact with us</p></td>
                    <td>
                      <ul style="list-style-type:none;">
                        <li>
                          Any other information necessary for the particular
                          contact and relationship with us.
                        </li>
                      </ul>
                    </td>
                  </tr>
                </table>

                <br />
                We do not collect sensitive information, health information or
                government identifiers.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                How do we collect personal information? From whom is it
                collected?
              </h2>
              <v-card-text>
                We usually collect your personal information directly from you
                unless it is unreasonable or impracticable to do so. When
                collecting personal information from you, we may collect it in
                the following ways:<br />
                <ul>
                  <li>
                    through your registration, access and/or use of our website,
                    app and other online presence, including, but not limited to
                    our KE Teacher and KE Classroom platforms;
                  </li>
                  <li>
                    during your communication with us through correspondence,
                    chats or email;
                  </li>
                  <li>during interaction with your use of our services;</li>
                  <li>through your participation in our surveys; and/or</li>
                  <li>
                    during conversations and meetings between you and our
                    representatives.
                  </li>
                </ul>
                <br /><br />
                We may also collect your personal information from a client or
                other third parties. For instance, we may have collected your
                personal information from a client who has engaged us as an
                agent to act on its behalf in its interactions with you.<br /><br />
                If you are a job applicant, we may collect personal information
                from a recruitment agency, your prior employers and others who
                may be able to assist us in our decision on whether to offer you
                employment.<br /><br />
                Where we collect personal information from a third party, we
                will take reasonable steps to either notify or ensure you are
                aware that we have collected your personal information and the
                circumstances of the collection.<br /><br />
                At all times, our collection of personal information is lawful
                and fair.<br /><br />
                Sometimes we receive unsolicited personal information. It is our
                general practice that unsolicited personal information is
                destroyed or de-identified unless it is unlawful to do so.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Why do we collect, hold, use, and disclose personal information?
              </h2>
              <v-card-text>
                Generally, we collect, hold, use, and disclose personal
                information so that we can create, deliver, and continuously
                improve our education programs. The specific purpose for which
                we collect and hold personal information will depend on the
                nature of relationship you have with us and is explained in the
                relevant privacy collection notice.<br /><br />
                Your personal information will not be shared, sold, rented, or
                disclosed other than as described in this privacy policy.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Do we use your personal information for direct marketing?
              </h2>
              <v-card-text>
                We may send you direct marketing communications and information
                about our services. This may take the form of emails, text
                messages, mail, or other forms of communication, in accordance
                with applicable laws including the Spam Act. You may opt-out of
                receiving marketing materials from us by contacting us using the
                details set out below or by using the opt-out facilities
                provided (e.g. an unsubscribe link).
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                To whom do we disclose your personal information?
              </h2>
              <v-card-text>
                In creating and delivering education programs, we may disclose
                your personal information to:<br />
                <ul>
                  <li>
                    our employees;
                  </li>
                  <li>
                    contractors, consultants and professional advisers, third
                    party suppliers, service providers, dealers and agents
                    (including website and information technology providers
                    and/or our business or in connection with our services);
                  </li>
                  <li>other third parties, such as accreditation agencies;</li>
                  <li>
                    payment systems operators (for instance, merchants receiving
                    card payments);
                  </li>
                  <li>
                    our existing or potential business partners;
                  </li>
                  <li>
                    specific third parties authorised by you to receive
                    information held by us; and/or
                  </li>
                  <li>
                    other persons, including, without limitation, government
                    agencies, regulatory bodies and law enforcement agencies, or
                    as required, authorised or permitted by law.
                  </li>
                </ul>
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Do we disclose, transfer or store your personal information
                outside Australia?
              </h2>
              <v-card-text>
                We may disclose personal information to entities outside of
                Australia, including in the United States with respect to our
                arrangements with data hosting and other IT service providers
                and our use of social media platforms. The privacy laws in the
                USA may not provide the same level of protection as the
                Australian privacy laws. When you provide your personal
                information to us, you consent to the disclosure, transfer and
                storage of your personal information outside of Australia and
                acknowledge that we are not required to ensure that overseas
                recipients manage that personal information in compliance with
                Australian privacy laws.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                How do we hold and secure your personal information?
              </h2>
              <v-card-text>
                We may hold your personal information in either electronic or
                hard copy form. We take reasonable steps to protect your
                personal information that we hold from misuse, interference, and
                loss and from unauthorised access, modification, or disclosure.
                Further, we use a number of physical, administrative, personnel
                and technical measures to protect your personal information
                including locked storage of paper records and password access
                rights to computerised records. We also conduct regular IT
                audits and monitor internal staff access to personal
                information. However, we cannot guarantee the security of your
                personal information.<br /><br />
                Further, we take reasonable steps to destroy or de-identify any
                personal information that is no longer needed unless otherwise
                required by law.<br /><br />
                As our website is linked to the internet and the internet is
                inherently insecure, we cannot provide any assurance regarding
                the security of transmission of information you communicate to
                us online. We also cannot guarantee that the information you
                supply will not be intercepted while being transmitted over the
                internet. Accordingly, any personal information or other
                information which you transmit to us online is transmitted at
                your own risk.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Does our website collect personal information? Does our website
                use cookies?
              </h2>
              <v-card-text>
                When you access and use our website, we may collect personal
                information about you.<br /><br />
                Generally, we do not use browsing information to identify you
                personally however we may record certain information about your
                use of our website, such as:<br />
                <ul>
                  <li>
                    which pages you visited;
                  </li>
                  <li>
                    the time and date of your visit; and
                  </li>
                  <li>
                    IP (Internet Protocol) address assigned to your computer.
                  </li>
                </ul>
                <br /><br />
                We may also collect your personal information by using cookies
                or other similar tracking technologies. A cookie is a small
                summary file containing a unique ID number. When you access our
                website, we may send your computer a cookie which will help us
                to serve you better, for instance, by automatically populating
                registration details. It also helps us monitor the products
                and/or services you view so that, if you agree, we can send you
                news about those products and services. You can disable cookies
                through your browser but this may impact your experience on our
                website.<br /><br />
                Information may also be used to analyse trends, track user
                movements, gather demographic information so that we can improve
                our products and services as well as our online presence.<br /><br />
                All personal information collected by cookies is handled in the
                same we manage other personal information as described in this
                privacy policy.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                What about third party links from our website?
              </h2>
              <v-card-text>
                Our website may contain links to other websites operated by
                third parties. These links are provided for your convenience. We
                do not have any control or rights over those websites so make no
                representations or warranties with respect to the contact and/or
                privacy practices of those websites. We encourage you to read
                their privacy policies (which may be substantially different
                from ours) prior to using their websites.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                What situations can you deal with us anonymously or by using a
                pseudonym?
              </h2>
              <v-card-text>
                There are limited circumstances where you can deal with us
                anonymously or by pseudonym. Some of these circumstances include
                in an emergency, providing us feedback on our services, making a
                whistle-blower alert, or where required by law.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                How do you access or correct your personal information?
              </h2>
              <v-card-text>
                If you require access to any personal information we hold about
                you, please contact our Privacy Officer (details below). We will
                consider your request and if appropriate, we will discuss with
                you a suitable way to give you access (for instance, by mailing,
                emailing it to you or inviting you to our offices to review the
                information to be accompanied by one of our representatives). A
                fee may apply to cover our administrative and other reasonable
                costs in providing you access. If we do need to apply a fee, we
                will let you know beforehand. We will not charge you for simply
                making a request to access or correct your personal
                information.<br /><br />
                Unfortunately, there may be times where we are not in a position
                to provide you access with all or some of your personal
                information. For instance, if giving you access will result in a
                breach of confidentiality or privacy of another person. We will
                give you written reasons if we are not in a position to provide
                you with access.<br /><br />
                If you believe any personal information we hold about you is
                incorrect or incomplete, please contact our Privacy Officer
                (details below). We will respond to a request for correction
                within 30 days. If we believe we cannot comply with your request
                to amend your personal information, we will provide you with
                written reasons.<br /><br />
                In both instances of requesting access and/or correcting your
                personal information, we will need to verify your identity.<br /><br />
                We also take reasonable steps to correct your personal
                information where we believe the information is not correct or
                updated. From time to time, we will ask you to verify your
                personal information held with us as up to date.<br /><br />
                We respect the right of every parent or carer (as relevant) to
                make decisions concerning their child. Generally, we will refer
                any requests for consent and notices in relation to the personal
                information of a student to the student’s parent or carer where
                the student is under 15. We will treat consent given by the
                parent or carer as consent given on behalf of the student under
                15 and notice to the parent or carer as notice given to the
                student under 15. We may, at our discretion:<br />
                <ul>
                  <li>
                    on the request of a student grant that student access to
                    information held by us about them or allow a student to give
                    or withhold consent to the use of their personal
                    information, independently of their parent or carer. This
                    would normally be done only when the maturity of the student
                    and/or the student’s circumstances warrant it; and
                  </li>
                  <li>
                    require the consent of the parent or carer if the student is
                    15 or over if the student lacks maturity or understanding to
                    provide consent themselves.
                  </li>
                </ul>
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                How do you make a complaint about a breach of privacy?
              </h2>
              <v-card-text>
                We treat all complaints confidentially.<br /><br />
                If you wish to make a complaint about the manner in which we
                have managed your personal information, please contact our
                Privacy Officer (details below). So that we can fully consider
                your complaint, please provide us with:<br /><br />
                <ul>
                  <li>your name;</li>
                  <li>
                    your relationship with us, for instance, whether you are a
                    client, job applicant or otherwise
                  </li>
                  <li>your contact details;</li>
                  <li>the nature of your complaint; and</li>
                  <li>
                    a description of the incident which has occurred leading to
                    your complaint.
                  </li>
                </ul>
                <br /><br />
                Upon receipt of your complaint, we will send you a formal
                acknowledgement of your complaint. Within 30 days of receipt of
                your complaint, our representative will contact you to discuss
                your concerns and outline options on how your complaint may be
                resolved. Our objective is to resolve your complaint in a timely
                and appropriate manner.<br /><br />
                If you are unhappy with our options, you may also contact the
                Office of Australian Information Commissioner (OAIC). For more
                details, please visit
                <a target="_blank" href="https://www.oaic.gov.au/"
                  >www.oaic.gov.au</a
                >
                or call the OAIC on 1300 336 002.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Contact Us
              </h2>
              <v-card-text>
                For further information about this privacy policy or practices,
                or to access or correct your personal information, or make a
                complaint, please contact us using the details set out below. We
                also welcome any feedback you may have on this privacy policy<br /><br />
                Ms Terri Martin<br />
                Privacy Officer<br />
                3/444 Gardeners Road<br />
                Alexandria NSW 2015<br />
                Email:
                <a href="mailto:terri@kimberlineducation.com.au"
                  >terri@kimberlineducation.com.au</a
                ><br />
                Phone: (02) 8592 6094<br /><br />
                This privacy policy was last updated on 30th October 2020
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h1 class="subheaders">
                Teachers, Educational Professionals and Affiliates Privacy
                Collection Notice
              </h1>
              <h2 class="subheaders">
                To whom are you disclosing your personal information?
              </h2>
              <v-card-text>
                Kimberlin Education Pty Limited ABN 86 154 894 470 (we, us, our)
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Why do we collect your personal information?
              </h2>
              <v-card-text>
                We collect your personal information to help us create, deliver
                engaging education programs that suit the needs and objectives
                of our business clients as well as evaluate and improve these
                programs. With your personal information, we are able to give
                you access to KE Classroom and KE Teacher and also allows you to
                participate in special promotions from time to time whilst we
                collect useful information from you to help us create and
                deliver engaging education programs. We also collect your
                personal information as authorised or required by law. Where the
                collection of information is authorised or required by law, we
                will let you know the specific law which allows us to collect
                your personal information at the time of collection.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Where do we collect your personal information from?
              </h2>
              <v-card-text>
                Generally, we collect your personal information directly from
                you via our website or through correspondence with us. There may
                be times when we collect your personal information from a
                business client who has engaged us as an agent to act on its
                behalf in its interactions with you.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                What are the consequences if some or all of your personal
                information is not collected?
              </h2>
              <v-card-text>
                Without the requested personal information, you may not be able
                to fully participate in our surveys, special promotions, KE
                Teacher or KE Classroom.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                To whom do we disclose your personal information?
              </h2>
              <v-card-text>
                We disclose your personal information to our employees and third
                parties that help us create and deliver our education programs
                (including technology information suppliers, contractors and
                professional advisers and business partners). There may be times
                we also disclose your personal information to our business
                clients. Personal information related to your use of KE
                Classroom may also be disclosed on an anonymised (or
                de-identified) basis to the Course Content Provider responsible
                for providing the relevant educational content or online
                interactive {{ lessonTerm }}. At all times, disclosure of your
                information will be governed by our privacy policy, law or with
                your prior consent.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Do we disclose your personal information overseas?
              </h2>
              <v-card-text>
                We may disclose personal information outside of Australia,
                including in the USA in connection with the use of cloud based
                data storage services and social media platforms. The privacy
                laws in the USA may not provide the same level of protection as
                the Australian privacy laws. When you provide your personal
                information to us, you consent to the disclosure, transfer and
                storage of your personal information outside of Australia and
                acknowledge that we are not required to ensure that overseas
                recipients manage that personal information in compliance with
                Australian privacy laws.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h1 class="subheaders">
                Further information
              </h1>
              <v-card-text>
                For more information about how we manage your personal
                information, including:<br />
                <ul>
                  <li>how to access and correct it;</li>
                  <li>
                    how to make a complaint and how we handle complaints; and
                  </li>
                  <li>
                    other useful information relating to our privacy practices,
                  </li>
                </ul>
                <br /><br />
                please see our privacy policy available at
                <a target="_blank" href="https://www.kimberlineducation.com.au"
                  >www.kimberlineducation.com.au</a
                ><br /><br />
                By providing your personal information to us, you consent to the
                collection, use, storage and disclosure of that information as
                described in the Privacy Policy and this collection notice.<br /><br />
                If you have any questions about this notice, our privacy policy
                or practices, please contact our Privacy Officer at:<br /><br />
                Ms Terri Martin<br />
                Privacy Officer<br />
                3/444 Gardeners Road<br />
                Alexandria NSW 2015<br />
                Email:
                <a href="mailto:terri@kimberlineducation.com.au"
                  >terri@kimberlineducation.com.au</a
                ><br />
                Phone: (02) 8592 6094
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h1 class="subheaders">
                Job Applicants Privacy Collection Notice
              </h1>
              <h2 class="subheaders">
                To whom are you disclosing your personal information?
              </h2>
              <v-card-text>
                Kimberlin Education Pty Limited ABN 86 154 894 470 (we, us, our)
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Why do we collect your personal information?
              </h2>
              <v-card-text>
                We collect your personal information in order for us to interact
                with you during the recruitment process and assess your
                application and suitability for a job with us. We also collect
                your personal information as authorised or required by law.
                Where the collection of information is authorised or required by
                law, we will let you know the specific law which allows us to
                collect your personal information at the time of collection.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Where do we collect your personal information from?
              </h2>
              <v-card-text>
                Generally, we collect your personal information directly from
                you. We may collect personal information from a recruitment
                agency, your prior employers and others who may be able to
                assist us in our decision on whether to offer you employment.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                What are the consequences if some or all of your personal
                information is not collected?
              </h2>
              <v-card-text>
                We may not be able to fully evaluate whether you are suitable
                for the job and may not be in a position to offer you
                employment.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                To whom do we disclose your personal information?
              </h2>
              <v-card-text>
                During the application process, you may be asked for your
                specific consent to the use and disclosure of certain personal
                information to your referees about any pre-employment medical
                examination or any aptitude or psychological testing. A refusal
                to provide any of this information, or to consent to its
                proposed disclosure may affect the success of your application.
                Your personal information may also be provided to service
                providers for purposes such as aptitude, psychological and
                medical testing as well as other human resources management
                activities, and/or people who you nominate to provide
                references. At all times, disclosure of your information will be
                governed by our privacy policy, law or with your prior consent.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Do we disclose your personal information overseas?
              </h2>
              <v-card-text>
                We may disclose personal information outside of Australia,
                including in the USA in connection with the use of cloud based
                data storage services and social media platforms. The privacy
                laws in the USA may not provide the same level of protection as
                the Australian privacy laws. When you provide your personal
                information to us, you consent to the disclosure, transfer and
                storage of your personal information outside of Australia and
                acknowledge that we are not required to ensure that overseas
                recipients manage that personal information in compliance with
                Australian privacy laws.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Further information
              </h2>
              <v-card-text>
                For more information about how we manage your personal
                information, including:<br />
                <ul>
                  <li>how to access and correct it;</li>
                  <li>
                    how to make a complaint and how we handle complaints; and
                  </li>
                  <li>
                    other useful information relating to our privacy practices,
                  </li>
                </ul>
                <br /><br />
                please see our privacy policy available at
                <a target="_blank" href="https://www.kimberlineducation.com.au"
                  >www.kimberlineducation.com.au</a
                ><br /><br />
                By providing your personal information to us, you consent to the
                collection, use, storage and disclosure of that information as
                described in the Privacy Policy and this collection notice.<br /><br />
                If you have any questions about this notice, our privacy policy
                or practices, please contact our Privacy Officer at:<br /><br />
                Ms Terri Martin<br />
                Privacy Officer<br />
                3/444 Gardeners Road<br />
                Alexandria NSW 2015<br />
                Email:
                <a href="mailto:terri@kimberlineducation.com.au"
                  >terri@kimberlineducation.com.au</a
                ><br />
                Phone: (02) 8592 6094
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h1 class="subheaders">
                Contractors Privacy Collection Notice
              </h1>
              <h2 class="subheaders">
                To whom are you disclosing your personal information?
              </h2>
              <v-card-text>
                Kimberlin Education Pty Limited ABN 86 154 894 470 (we, us, our)
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Why do we collect your personal information?
              </h2>
              <v-card-text>
                We collect your personal information to help us create, deliver
                engaging education programs that suit the needs and objectives
                of our business clients as well as evaluate and improve these
                programs. With your personal information, we can establish and
                maintain our business relationship with you and to carry out our
                duties under this relationship with you, for instance, pay you.
                We also collect your personal information as authorised or
                required by law. Where the collection of information is
                authorised or required by law, we will let you know the specific
                law which allows us to collect your personal information at the
                time of collection.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Where do we collect your personal information from?
              </h2>
              <v-card-text>
                Generally, we collect your personal information directly from
                you through correspondence with us.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                What are the consequences if some or all of your personal
                information is not collected?
              </h2>
              <v-card-text>
                Without the requested personal information, we may not be able
                to maintain our business relationship with you and/or carry out
                our duties under this relationship.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                To whom do we disclose your personal information?
              </h2>
              <v-card-text>
                We disclose your personal information to our employees and third
                parties that help us create and deliver our education programs
                (including technology information suppliers, contractors and
                professional advisers and business partners) as well as other
                contractors who may also be providing us with similar support.
                There may be times we also disclose your personal information to
                our business clients. At all times, disclosure of your
                information will be governed by our privacy policy, law or with
                your prior consent.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Do we disclose your personal information overseas?
              </h2>
              <v-card-text>
                We may disclose personal information outside of Australia,
                including in the USA in connection with the use of cloud based
                data storage services and social media platforms. The privacy
                laws in the USA may not provide the same level of protection as
                the Australian privacy laws. When you provide your personal
                information to us, you consent to the disclosure, transfer and
                storage of your personal information outside of Australia and
                acknowledge that we are not required to ensure that overseas
                recipients manage that personal information in compliance with
                Australian privacy laws.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Further information
              </h2>
              <v-card-text>
                For more information about how we manage your personal
                information, including:<br />
                <ul>
                  <li>how to access and correct it;</li>
                  <li>
                    how to make a complaint and how we handle complaints; and
                  </li>
                  <li>
                    other useful information relating to our privacy practices,
                  </li>
                </ul>
                <br /><br />
                please see our privacy policy available at
                <a target="_blank" href="https://www.kimberlineducation.com.au"
                  >www.kimberlineducation.com.au</a
                ><br /><br />
                By providing your personal information to us, you consent to the
                collection, use, storage and disclosure of that information as
                described in the Privacy Policy and this collection notice.<br /><br />
                If you have any questions about this notice, our privacy policy
                or practices, please contact our Privacy Officer at:<br /><br />
                Ms Terri Martin<br />
                Privacy Officer<br />
                3/444 Gardeners Road<br />
                Alexandria NSW 2015<br />
                Email:
                <a href="mailto:terri@kimberlineducation.com.au"
                  >terri@kimberlineducation.com.au</a
                ><br />
                Phone: (02) 8592 6094
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h1 class="subheaders">
                Students Privacy Collection Notice
              </h1>
              <h2 class="subheaders">
                To whom are you disclosing your personal information?
              </h2>
              <v-card-text>
                Kimberlin Education Pty Limited ABN 86 154 894 470 (we, us, our)
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Why do we collect your personal information?
              </h2>
              <v-card-text>
                We collect your personal information to help us create, deliver
                engaging education programs that suit the needs and objectives
                of our business clients as well as evaluate and improve these
                programs. With your personal information, we are able to provide
                you with opportunities to appear and participate in educational
                material and participate in surveys from time to time. We also
                collect your personal information as authorised or required by
                law. Where the collection of information is authorised or
                required by law, we will let you know the specific law which
                allows us to collect your personal information at the time of
                collection.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Where do we collect your personal information from?
              </h2>
              <v-card-text>
                Generally, we collect your personal information directly from
                you through correspondence with us. There may be times when we
                collect your personal information from a business client who has
                engaged us as an agent to act on its behalf in its interactions
                with you.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                What are the consequences if some or all of your personal
                information is not collected?
              </h2>
              <v-card-text>
                Without the requested personal information, we may not be able
                to provide you with opportunities to appear and participate in
                educational material and surveys.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                To whom do we disclose your personal information?
              </h2>
              <v-card-text>
                We disclose your personal information to our employees and third
                parties that help us create and deliver our education programs
                (including technology information suppliers, contractors and
                professional advisers and business partners). There may be times
                we also disclose your personal information to our business
                clients. At all times, disclosure of your information will be
                governed by our privacy policy, law or with your prior consent.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Do we disclose your personal information overseas?
              </h2>
              <v-card-text>
                We may disclose personal information outside of Australia,
                including in the USA in connection with the use of cloud based
                data storage services and social media platforms. The privacy
                laws in the USA may not provide the same level of protection as
                the Australian privacy laws. When you provide your personal
                information to us, you consent to the disclosure, transfer and
                storage of your personal information outside of Australia and
                acknowledge that we are not required to ensure that overseas
                recipients manage that personal information in compliance with
                Australian privacy laws.
              </v-card-text>
            </v-card>
            <v-card flat class="mt-4 legal">
              <h2 class="subheaders">
                Further information
              </h2>
              <v-card-text>
                For more information about how we manage your personal
                information, including:<br />
                <ul>
                  <li>how to access and correct it;</li>
                  <li>
                    how to make a complaint and how we handle complaints; and
                  </li>
                  <li>
                    other useful information relating to our privacy practices,
                  </li>
                </ul>
                <br /><br />
                please see our privacy policy available at
                <a target="_blank" href="https://www.kimberlineducation.com.au"
                  >www.kimberlineducation.com.au</a
                ><br /><br />
                By providing your personal information to us, you consent to the
                collection, use, storage and disclosure of that information as
                described in the Privacy Policy and this collection notice.<br /><br />
                If you have any questions about this notice, our privacy policy
                or practices, please contact our Privacy Officer at:<br /><br />
                Ms Terri Martin<br />
                Privacy Officer<br />
                3/444 Gardeners Road<br />
                Alexandria NSW 2015<br />
                Email:
                <a href="mailto:terri@kimberlineducation.com.au"
                  >terri@kimberlineducation.com.au</a
                ><br />
                Phone: (02) 8592 6094<br /><br />
                These privacy collection notices were last updated on 30th
                October 2020
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </transition>
</template>

<script>
import HeaderLoggedOut from '@/modules/auth/views/HeaderLoggedOut'
import { mapState } from 'vuex'

export default {
  components: {
    HeaderLoggedOut
  },
  computed: {
    ...mapState(['lessonTerm'])
  }
}
</script>

<style scoped lang="scss">
html body .legal {
  .subheaders {
    color: var(--v-primary-base);
  }

  a {
    color: rgba(0, 0, 0, 0.87);
  }

  h2 {
    font-size: 18px !important;
  }
}

.policy-table,
td {
  border: 1px solid black;
  border-collapse: collapse;

  td {
    padding: 7px;
  }
}
</style>
